import posthog from 'posthog-js';

/**
 * Product analytics — the ONE place the app talks to PostHog.
 *
 * Everything is key-gated: without `VITE_POSTHOG_KEY` every function here is a
 * silent no-op (no network, no console noise) so local dev, tests, and any
 * environment without a configured key behave exactly as before. Components
 * never import `posthog-js` directly — they call `track('event', props)` and
 * `identify(id)` so the vendor stays swappable and event names stay auditable.
 *
 * Funnel spec: FINAL_STATE §7 (platform baseline / instrumentation),
 * Wingspan cut rank 6 (WEDGE_ROADMAP §0.0) — "can't see anyone approach the
 * machine". Closes Latimer-Woods-Tech/xico-city issue 351.
 */

/** Product events. Names are stable contracts consumed by PostHog dashboards. */
export type AnalyticsEvent =
  | 'listing_viewed'
  | 'preview_played'
  | 'checkout_started'
  | 'signup_completed'
  | 'upload_confirmed'
  | 'purchase_page_viewed'
  | 'certificate_viewed'
  // F-054 creator directory + onboarding (Wingspan cut 11).
  | 'directory_viewed'
  | 'onboarding_started'
  | 'onboarding_completed'
  // The bench funnel — the producer's path from room to sale (Build Plan 01 P3,
  // ROOMS §5 Room 1). `upload_confirmed` is already the "beat in" step; these
  // close the rest: enter the room, confirm the cuts, list the pack. The buyer-
  // side `sale_completed` fires only on Stripe's post-payment redirect, so it is
  // a settled payment, never an optimistic client guess.
  | 'bench_entered'
  | 'cuts_confirmed'
  | 'pack_listed'
  | 'sale_completed'
  // F-055 the co-sign trust flywheel — a producer stakes their name on a pack.
  | 'cosign_added';

/** Event properties are plain JSON scalars — never PII beyond the authed user id. */
export type AnalyticsProps = Record<string, string | number | boolean | null | undefined>;

const KEY = import.meta.env.VITE_POSTHOG_KEY;
const HOST = import.meta.env.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com';

let ready = false;

/** True once PostHog has been initialised with a real key (analytics is live). */
export function isAnalyticsEnabled(): boolean {
  return ready;
}

/**
 * Initialise PostHog exactly once, only when a key is configured. Safe to call
 * on every app boot; a missing key makes this a no-op. Pageviews are captured
 * manually on route change (see `trackPageview`) because this is an SPA.
 */
export function initAnalytics(): void {
  if (ready || !KEY) return;
  try {
    posthog.init(KEY, {
      api_host: HOST,
      capture_pageview: false, // SPA — we emit $pageview manually on navigation.
      capture_pageleave: true,
      autocapture: false, // Explicit product events only; no DOM scraping.
      persistence: 'localStorage+cookie',
      person_profiles: 'identified_only',
    });
    ready = true;
  } catch {
    // Never let analytics boot crash the app.
    ready = false;
  }
}

/** Capture a product event with its properties. No-op until analytics is live. */
export function track(event: AnalyticsEvent, props: AnalyticsProps = {}): void {
  if (!ready) return;
  try {
    posthog.capture(event, prune(props));
  } catch {
    // swallow — analytics must never break a user flow.
  }
}

/** Emit an SPA pageview for the given path. No-op until analytics is live. */
export function trackPageview(path: string): void {
  if (!ready) return;
  try {
    posthog.capture('$pageview', { $current_url: path });
  } catch {
    // swallow
  }
}

/**
 * Associate subsequent events with the authed user id. Call on session load.
 * Only the opaque user id is sent — no email/name (see NO-PII rule).
 */
export function identify(userId: string): void {
  if (!ready || !userId) return;
  try {
    posthog.identify(userId);
  } catch {
    // swallow
  }
}

/** Clear the identified person on sign-out so the next session starts anonymous. */
export function resetAnalytics(): void {
  if (!ready) return;
  try {
    posthog.reset();
  } catch {
    // swallow
  }
}

/** Drop undefined props so PostHog payloads stay tidy. */
function prune(props: AnalyticsProps): AnalyticsProps {
  const out: AnalyticsProps = {};
  for (const [k, v] of Object.entries(props)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}
